/**
 * Inventory Management Module - Ready-to-Sale Batches Only
 *
 * This module displays ONLY batches with status "READY_FOR_SALE" from the
 * biogas production system (biogas_batch_management table).
 *
 * Features:
 * - Read-only view (no manual batch creation)
 * - Automatic sync from biogas-service
 * - Quality grade mapping (PREMIUM→A, STANDARD→B, BASIC→C)
 * - PESO compliance tracking
 * - Expiry alerts and low stock warnings
 *
 * Data Flow: biogas-service (READY_FOR_SALE) → Frontend Display
 * API Endpoint: GET /biogas-service/api/v1/batch-management/batches?status=READY_FOR_SALE
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Package, Search, Filter, Edit, MapPin, Calendar, AlertTriangle,
  CheckCircle, Clock, BarChart3, TrendingUp, Shield, Eye, Award, X
} from 'lucide-react';
import { toast } from 'sonner';
import { salesService } from '../services/salesService';
import { InventoryItem, InventoryFilters } from '../types';
import { formatCurrency } from '@/lib/utils';
import { PesoComplianceDialog } from '@/components/inventory/batches/PesoComplianceDialog';
import { UpdateQualityDialog } from '@/components/inventory/batches/UpdateQualityDialog';
import { BatchDetailsDialog } from '@/components/inventory/batches/BatchDetailsDialog';
import { ExpiringBatchesWidget, type ExpiringBatchSummary } from '@/components/inventory/batches/ExpiringBatchesWidget';
import { ExpiringBatchesAlertTab, type ExpiringBatch } from '@/components/inventory/batches/ExpiringBatchesAlertTab';
import { ExpiringBatchDetailsDialog } from '@/components/inventory/batches/ExpiringBatchDetailsDialog';
import type { BatchResponse } from '@/types/inventory';
import { isPesoCertificateExpiringSoon } from '@/types/inventory';
import { BatchFiltersPanel } from '@/components/inventory/batches/BatchFiltersPanel';
import type { BatchFilters } from '@/types/availableBatches';

interface InventoryCardProps {
  item: InventoryItem;
  onMarkPesoCompliant?: (item: InventoryItem) => void;
}

const InventoryCard: React.FC<InventoryCardProps> = ({ item, onMarkPesoCompliant }) => {
  const [showViewDialog, setShowViewDialog] = useState(false);

  const availabilityPercentage = (item.availableQuantity / item.quantity) * 100;
  const isLowStock = availabilityPercentage < 20;
  const isPesoExpiring = item.pesoCompliance?.expiryDate ?
    isPesoCertificateExpiringSoon(item.pesoCompliance.expiryDate.toISOString()) : false;
  const isAllocated = item.availableQuantity < item.quantity;
  const hasPesoCertificate = item.pesoCompliance?.status === 'valid' || item.pesoCompliance?.status === 'expired';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg">{item.batchNumber}</CardTitle>
            <div className="flex items-center space-x-2">
              {item.availableQuantity === 0 ? (
                <Badge className="bg-red-200 text-red-800">consumed</Badge>
              ) : isLowStock ? (
                <Badge className="bg-orange-200 text-orange-800">low stock</Badge>
              ) : (
                <Badge className={getStatusColor(item.status)}>{item.status}</Badge>
              )}
              <Badge variant="outline">{item.gasType}</Badge>
              {isPesoExpiring && <Badge className="bg-orange-100 text-orange-800">PESO Expiring</Badge>}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-600">{item.availableQuantity === 0 ? 'Consumed' : 'Available'}</div>
            <div className={`font-semibold ${item.availableQuantity === 0 ? 'text-red-600' : 'text-green-600'}`}>
              {item.availableQuantity.toLocaleString()} {item.unit}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Total</div>
            <div className="font-semibold">{item.quantity.toLocaleString()} {item.unit}</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Availability</span>
            <span className={isLowStock ? 'text-red-600' : 'text-green-600'}>
              {availabilityPercentage.toFixed(1)}%
            </span>
          </div>
          <Progress value={availabilityPercentage} className="h-2" />
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center text-gray-600">
            <Shield className="w-4 h-4 mr-2" />
            PESO: {item.pesoCompliance.status} (Exp: {new Date(item.pesoCompliance.expiryDate).toLocaleDateString()})
          </div>
          <div className="flex items-center text-gray-600">
            <MapPin className="w-4 h-4 mr-2" />
            {item.location.plantId} - {item.location.tankId}
          </div>
          <div className="flex items-center text-gray-600">
            <Calendar className="w-4 h-4 mr-2" />
            Produced: {new Date(item.productionDate).toLocaleDateString()}
          </div>
        </div>

        <div className="pt-2 border-t">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Value</span>
            <span className="font-semibold">₹{(item.availableQuantity * (item.pricePerUnit || 0)).toLocaleString()}</span>
          </div>
        </div>

        {/* US-BATCH-003 & US-BATCH-004: Action Buttons */}
        <div className="pt-3 space-y-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 flex items-center justify-center gap-2"
              onClick={() => setShowViewDialog(true)}
            >
              <Eye className="w-4 h-4" />
              View Details
            </Button>
          </div>
          {/* US-BATCH-004: Mark PESO Compliant Button */}
          <Button
            variant={hasPesoCertificate ? "outline" : "default"}
            size="sm"
            className="w-full flex items-center justify-center gap-2"
            onClick={() => onMarkPesoCompliant?.(item)}
            title={
              hasPesoCertificate
                ? "Update PESO certificate"
                : "Mark as PESO compliant"
            }
          >
            <Award className="w-4 h-4" />
            {hasPesoCertificate ? "Update PESO" : "Mark PESO Compliant"}
          </Button>
          {isPesoExpiring && hasPesoCertificate && (
            <div className="text-xs text-orange-600 flex items-center gap-1 px-2">
              <AlertTriangle className="w-3 h-3" />
              Certificate expires soon
            </div>
          )}
        </div>

        {/* View Details Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Batch Details: {item.batchNumber}
              </DialogTitle>
              <DialogDescription>Complete information for this batch</DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Status Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <Badge className={`mt-1 w-fit ${getStatusColor(item.status)}`}>{item.status}</Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Product Type</label>
                  <Badge variant="outline" className="mt-1 w-fit">{item.productType || item.gasType}</Badge>
                </div>
              </div>

              {/* Quantity Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Quantity Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Total Quantity</div>
                    <div className="font-semibold text-lg">{item.quantity.toLocaleString()} {item.unit}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Available</div>
                    <div className="font-semibold text-lg text-green-600">{item.availableQuantity.toLocaleString()} {item.unit}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Reserved</div>
                    <div className="font-semibold text-lg text-orange-600">{(item.quantity - item.availableQuantity).toLocaleString()} {item.unit}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Availability</span>
                    <span className={((item.availableQuantity / item.quantity) * 100) < 20 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                      {((item.availableQuantity / item.quantity) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={(item.availableQuantity / item.quantity) * 100} className="h-2" />
                </div>
              </div>

              {/* Pricing Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Pricing Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Price per Unit</div>
                    <div className="font-semibold">₹{item.pricePerUnit.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Value</div>
                    <div className="font-semibold">₹{(item.quantity * item.pricePerUnit).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Available Value</div>
                    <div className="font-semibold text-green-600">₹{(item.availableQuantity * item.pricePerUnit).toLocaleString()}</div>
                  </div>
                </div>
              </div>

              {/* Location & Compliance Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Location & Compliance</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-600">Storage Location</div>
                      <div className="font-medium">{item.location.plantId} - {item.location.tankId}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-600">PESO Compliance</div>
                      <div className="font-medium">
                        {item.pesoCompliance.status}
                        <span className="text-xs text-gray-500 ml-2">
                          (Expires: {new Date(item.pesoCompliance.expiryDate).toLocaleDateString()})
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-600">Production Date</div>
                      <div className="font-medium">{new Date(item.productionDate).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Grade Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Quality Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Quality Grade</div>
                    <Badge className="mt-1">{item.qualityGrade || 'Not Set'}</Badge>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Purity</div>
                    <div className="font-semibold">{item.purityPercentage || 'N/A'}%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" className="flex-1" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'available': return 'bg-green-100 text-green-800';
    case 'reserved': return 'bg-yellow-100 text-yellow-800';
    case 'sold': return 'bg-blue-100 text-blue-800';
    case 'expired': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const InventoryManagement: React.FC = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<InventoryFilters>({});
  const [batchFilters, setBatchFilters] = useState<BatchFilters>({
    productType: 'ALL',
    qualityGrades: [],
    pesoCompliant: null,
    expiryRange: 'ALL',
    storageLocation: '',
    lowStockOnly: false,
    minPurity: undefined,
    maxPurity: undefined,
    minQuantity: undefined,
    clusterId: undefined,
    minH2SContent: undefined,
    maxH2SContent: undefined
  });
  const [selectedBatchForUpdate, setSelectedBatchForUpdate] = useState<BatchResponse | null>(null);
  const [showUpdateQualityDialog, setShowUpdateQualityDialog] = useState(false);
  const [selectedBatchForPeso, setSelectedBatchForPeso] = useState<BatchResponse | null>(null);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  // US-BATCH-006: Expiring Batches State
  const [expiringBatches, setExpiringBatches] = useState<ExpiringBatch[]>([]);
  const [expiringBatchesSummary, setExpiringBatchesSummary] = useState<ExpiringBatchSummary | undefined>();
  const [expiringBatchesLoading, setExpiringBatchesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('grid');
  const [showPesoDialog, setShowPesoDialog] = useState(false);

  // US-BATCH-006: Batch Details Dialog State
  const [selectedBatchForDetails, setSelectedBatchForDetails] = useState<string | number | null>(null);
  const [showBatchDetailsDialog, setShowBatchDetailsDialog] = useState(false);
  const [batchDetailsReadOnly, setBatchDetailsReadOnly] = useState(false);

  // Expiring Batch Details Dialog State
  const [selectedExpiringBatch, setSelectedExpiringBatch] = useState<ExpiringBatch | null>(null);
  const [showExpiringBatchDetailsDialog, setShowExpiringBatchDetailsDialog] = useState(false);

  // Auto-refresh state for real-time updates
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadInventory = async (retryCount = 0) => {
      try {
        setLoadError(null);
        const response = await salesService.getInventory(filters);
        if (cancelled) return;
        if (response.success) {
          setInventory(response.data);
          setFilteredInventory(response.data);
          // Load expiring batches when inventory loads
          loadExpiringBatches(response.data);
          setLastUpdated(new Date());
        } else {
          // API returned but with success: false — retry up to 2 times
          if (retryCount < 2) {
            console.warn(`⚠️ Inventory load attempt ${retryCount + 1} failed, retrying...`);
            await new Promise(r => setTimeout(r, 2000));
            if (!cancelled) return loadInventory(retryCount + 1);
          } else {
            setLoadError(response.message || 'Failed to load inventory. Please click Refresh to try again.');
            toast.error('Failed to load inventory', {
              description: 'The server returned an error. Please try refreshing.',
            });
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading inventory:', error);
        if (retryCount < 2) {
          console.warn(`⚠️ Inventory load attempt ${retryCount + 1} threw error, retrying...`);
          await new Promise(r => setTimeout(r, 2000));
          if (!cancelled) return loadInventory(retryCount + 1);
        } else {
          setLoadError('Failed to load inventory. Please click Refresh to try again.');
          toast.error('Failed to load inventory', {
            description: 'A network error occurred. Please try refreshing.',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadInventory();

    return () => { cancelled = true; };
  }, [filters]);

  // Auto-refresh: Automatically fetch READY_FOR_SALE batches every 60 seconds
  // This ensures new batches appear when status changes to READY_FOR_SALE
  // Note: Does NOT overwrite existing data on failure — preserves last successful state
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const refreshInterval = setInterval(async () => {
      try {
        setIsRefreshing(true);
        const response = await salesService.getInventory(filters);
        if (response.success) {
          setInventory(response.data);
          setFilteredInventory(response.data);
          loadExpiringBatches(response.data);
          setLastUpdated(new Date());
          setLoadError(null);
          console.log('✅ Inventory auto-refreshed - READY_FOR_SALE batches updated');
        } else {
          // Silently ignore auto-refresh failures — keep showing existing data
          console.warn('⚠️ Auto-refresh failed, keeping existing data');
        }
      } catch (error) {
        // Silently ignore auto-refresh failures — keep showing existing data
        console.warn('⚠️ Auto-refresh error, keeping existing data:', error);
      } finally {
        setIsRefreshing(false);
      }
    }, 60000); // Refresh every 60 seconds (reduced from 30s to avoid server overload)

    return () => clearInterval(refreshInterval);
  }, [filters, autoRefreshEnabled]);

  // Smart keyword filter — returns a filter function for exact keyword matches
  const getSmartKeywordFilter = (
    search: string,
    today: Date,
    thirtyDaysFromNow: Date
  ): ((item: InventoryItem) => boolean) | null => {
    switch (search) {
      case 'available':
        return (item) => {
          // Available = availableQuantity > 0 AND NOT low stock (>= 20% availability)
          const availabilityPct = item.quantity > 0 ? (item.availableQuantity / item.quantity) * 100 : 0;
          return item.availableQuantity > 0 && availabilityPct >= 20;
        };
      case 'low stock':
        return (item) => {
          // Low stock = 0 < availableQuantity < 20% of total
          const availabilityPct = item.quantity > 0 ? (item.availableQuantity / item.quantity) * 100 : 0;
          return item.availableQuantity > 0 && availabilityPct < 20;
        };
      case 'consumed':
        return (item) => {
          // Consumed = availableQuantity === 0
          return item.availableQuantity === 0;
        };
      case 'reserved':
        return (item) => item.status === 'reserved';
      case 'sold':
        return (item) => item.status === 'sold';
      case 'expired':
        return (item) => item.status === 'expired' || (item.expiryDate ? new Date(item.expiryDate) < today : false);
      case 'expiring soon':
        return (item) => {
          if (!item.expiryDate) return false;
          const expiry = new Date(item.expiryDate);
          return expiry > today && expiry <= thirtyDaysFromNow;
        };
      case 'peso expiring':
        return (item) => {
          if (!item.pesoCompliance?.expiryDate) return false;
          const pesoExpiry = new Date(item.pesoCompliance.expiryDate);
          return pesoExpiry > today && pesoExpiry <= thirtyDaysFromNow;
        };
      case 'peso valid':
        return (item) => item.pesoCompliance?.status === 'valid';
      case 'peso expired':
        return (item) => item.pesoCompliance?.status === 'expired';
      case 'grade a':
        return (item) => item.qualityGrade === 'A';
      case 'grade b':
        return (item) => item.qualityGrade === 'B';
      case 'grade c':
        return (item) => item.qualityGrade === 'C';
      case 'biogas':
        return (item) => item.gasType === 'biogas';
      case 'compressed':
        return (item) => item.gasType === 'compressed';
      case 'liquefied':
        return (item) => item.gasType === 'liquefied';
      case 'high purity':
        return (item) => (item.quality?.methaneContent ?? 0) > 90;
      default:
        return null;
    }
  };

  // Apply search and batch filters to inventory
  useEffect(() => {
    let filtered = [...inventory];

    // Apply search term filter
    if (searchTerm) {
      const trimmedSearch = searchTerm.trim().toLowerCase();

      // Check if user is searching by availability percentage (e.g., "100", "100%", "50.5%")
      const availabilityMatch = trimmedSearch.match(/^(\d+\.?\d*)%?$/);

      if (availabilityMatch) {
        // Availability percentage search — exact match on integer part
        const targetPercent = Math.floor(parseFloat(availabilityMatch[1]));
        filtered = filtered.filter(item => {
          const availabilityPercent = item.quantity > 0
            ? (item.availableQuantity / item.quantity) * 100
            : 0;
          return Math.floor(availabilityPercent) === targetPercent;
        });
      } else {
        // Smart keyword-based filtering
        const today = new Date();
        const thirtyDaysFromNow = new Date(today);
        thirtyDaysFromNow.setDate(today.getDate() + 30);

        // Check for exact keyword matches first for better UX
        const keywordFilter = getSmartKeywordFilter(trimmedSearch, today, thirtyDaysFromNow);
        if (keywordFilter) {
          filtered = filtered.filter(keywordFilter);
        } else {
          // General text search — matches batch number, product type, gas type, location, status, and smart labels
          const queryWords = trimmedSearch.replace(/[_-]/g, ' ').split(/\s+/);
          filtered = filtered.filter(item => {
            try {
              const availabilityPercent = item.quantity > 0
                ? (item.availableQuantity / item.quantity) * 100
                : 0;
              const isPesoExpiring = item.pesoCompliance?.expiryDate
                ? new Date(item.pesoCompliance.expiryDate) > today && new Date(item.pesoCompliance.expiryDate) <= thirtyDaysFromNow
                : false;
              const isExpiringSoon = item.expiryDate
                ? new Date(item.expiryDate) > today && new Date(item.expiryDate) <= thirtyDaysFromNow
                : false;
              const methaneContent = item.quality?.methaneContent ?? 0;

              // Calculate availability status
              const availabilityStatus = item.availableQuantity === 0
                ? 'consumed'
                : availabilityPercent >= 20
                ? 'available'
                : 'low stock';

              const searchableText = [
                item.id || '',
                item.batchNumber || '',
                item.gasType || '',
                item.productType || '',
                item.status || '',
                availabilityStatus,
                item.qualityGrade ? `grade ${item.qualityGrade}` : '',
                item.location?.plantId || '',
                item.location?.tankId || '',
                item.manufacturingUnitName || '',
                item.manufacturingUnitLocation || '',
                item.unit || '',
                isPesoExpiring ? 'peso expiring' : '',
                item.pesoCompliance?.status === 'valid' ? 'peso valid' : '',
                item.pesoCompliance?.status === 'expired' ? 'peso expired' : '',
                isExpiringSoon ? 'expiring soon' : '',
                methaneContent > 90 ? 'high purity' : '',
              ].join(' ').toLowerCase().replace(/[_-]/g, ' ');
              return queryWords.every(word => searchableText.includes(word));
            } catch {
              return false;
            }
          });
        }
      }
    }

    // Quality grades filter (by letter grade A, B, C)
    if (batchFilters.qualityGrades && batchFilters.qualityGrades.length > 0) {
      filtered = filtered.filter(item => {
        try {
          return item.qualityGrade && batchFilters.qualityGrades.includes(item.qualityGrade);
        } catch {
          return false;
        }
      });
    }

    // Methane content (Purity) filter
    if (batchFilters.minPurity !== undefined || batchFilters.maxPurity !== undefined) {
      filtered = filtered.filter(item => {
        try {
          const methaneContent = item.quality?.methaneContent ?? 0;
          const meetsMin = batchFilters.minPurity !== undefined ? methaneContent >= batchFilters.minPurity : true;
          const meetsMax = batchFilters.maxPurity !== undefined ? methaneContent <= batchFilters.maxPurity : true;
          return meetsMin && meetsMax;
        } catch {
          return false;
        }
      });
    }

    // Biogas Volume (Quantity) filter
    if (batchFilters.minQuantity !== undefined && batchFilters.minQuantity > 0) {
      filtered = filtered.filter(item => {
        try {
          return item.availableQuantity >= batchFilters.minQuantity!;
        } catch {
          return false;
        }
      });
    }

    // Cluster ID (Location) filter
    if (batchFilters.clusterId && batchFilters.clusterId.trim()) {
      filtered = filtered.filter(item => {
        try {
          const clusterIdStr = (item.location?.plantId || '').toLowerCase();
          return clusterIdStr.includes(batchFilters.clusterId!.toLowerCase());
        } catch {
          return false;
        }
      });
    }

    // H2S Content (Hydrogen Sulfide) filter - ppm (parts per million)
    if (batchFilters.minH2SContent !== undefined || batchFilters.maxH2SContent !== undefined) {
      filtered = filtered.filter(item => {
        try {
          const h2sContent = item.quality?.pressure ?? 15; // Default if not found
          const meetsMin = batchFilters.minH2SContent !== undefined ? h2sContent >= batchFilters.minH2SContent : true;
          const meetsMax = batchFilters.maxH2SContent !== undefined ? h2sContent <= batchFilters.maxH2SContent : true;
          return meetsMin && meetsMax;
        } catch {
          return false;
        }
      });
    }

    // Production date range filter
    if (batchFilters.expiryRange && batchFilters.expiryRange !== 'ALL') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      const sixtyDaysFromNow = new Date(today);
      sixtyDaysFromNow.setDate(today.getDate() + 60);

      filtered = filtered.filter(item => {
        try {
          let productionDate: Date | null = null;

          if (item.expiryDate instanceof Date) {
            productionDate = item.expiryDate;
          } else if (typeof item.expiryDate === 'string') {
            productionDate = new Date(item.expiryDate);
          }

          if (!productionDate || isNaN(productionDate.getTime())) {
            return false;
          }

          productionDate.setHours(0, 0, 0, 0);

          switch (batchFilters.expiryRange) {
            case 'EXPIRING_SOON':
              return productionDate <= thirtyDaysFromNow && productionDate >= today;
            case '30_60_DAYS':
              return productionDate > thirtyDaysFromNow && productionDate <= sixtyDaysFromNow;
            case 'OVER_60_DAYS':
              return productionDate > sixtyDaysFromNow;
            default:
              return true;
          }
        } catch {
          return false;
        }
      });
    }

    setFilteredInventory(filtered);
  }, [inventory, searchTerm, batchFilters]);

  /**
   * US-BATCH-006: Load expiring batches from inventory
   * Converts InventoryItem to ExpiringBatch format and calculates urgency
   */
  const loadExpiringBatches = (items: InventoryItem[]) => {
    setExpiringBatchesLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Filter and convert inventory items that are expiring within 30 days
      // IMPORTANT: Exclude already-expired batches (daysRemaining < 0)
      const expiring = items
        .map(item => {
          const expiryDate = new Date(item.expiryDate || item.pesoCompliance.expiryDate);
          expiryDate.setHours(0, 0, 0, 0);

          const daysRemaining = Math.floor(
            (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
          );

          // Determine urgency level based on days remaining
          // CRITICAL: 0-7 days remaining (not yet expired)
          // HIGH (Warning): >7 to <15 days remaining
          // MEDIUM (Normal): >=15 to <=30 days remaining
          // Expired batches (daysRemaining < 0) are excluded from this list
          let urgencyLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' = 'LOW';
          if (daysRemaining >= 0 && daysRemaining < 7) urgencyLevel = 'CRITICAL';
          else if (daysRemaining >= 7 && daysRemaining < 15) urgencyLevel = 'HIGH';
          else if (daysRemaining >= 15 && daysRemaining <= 30) urgencyLevel = 'MEDIUM';

          return {
            id: parseInt(item.id),
            batchNumber: item.batchNumber,
            productType: item.productType || item.gasType,
            availableQuantity: item.availableQuantity,
            currentQuantity: item.quantity,
            quantityUnit: item.unit,
            qualityGrade: item.qualityGrade || 'A',
            purityPercentage: item.purityPercentage || 95,
            expiryDate: expiryDate.toISOString().split('T')[0],
            daysRemaining,
            urgencyLevel,
            storageLocation: `${item.location.plantId} - ${item.location.tankId}`,
            pesoCompliant: item.pesoCompliance?.status === 'valid',
            pesoCertificateNumber: item.pesoCompliance?.certificateNumber,
            productionDate: item.productionDate instanceof Date
              ? item.productionDate.toISOString().split('T')[0]
              : item.productionDate,
          } as ExpiringBatch;
        })
        // FIXED: Exclude expired batches (daysRemaining < 0) and only show batches expiring in next 30 days
        .filter(batch => batch.daysRemaining >= 0 && batch.daysRemaining <= 30)
        .sort((a, b) => a.daysRemaining - b.daysRemaining); // Sort by urgent first

      setExpiringBatches(expiring);

      // Calculate summary - only counts non-expired batches
      if (expiring.length > 0) {
        const summary: ExpiringBatchSummary = {
          totalExpiringCount: expiring.length,
          // CRITICAL: 0-7 days remaining (not yet expired)
          criticalCount: expiring.filter(b => b.daysRemaining >= 0 && b.daysRemaining < 7).length,
          // WARNING (HIGH): >7 to <15 days remaining
          warningCount: expiring.filter(b => b.daysRemaining >= 7 && b.daysRemaining < 15).length,
          // NORMAL (MEDIUM): >=15 to <=30 days remaining
          normalCount: expiring.filter(b => b.daysRemaining >= 15 && b.daysRemaining <= 30).length,
          // Expired batches are NOT included in the expiring list, so count is always 0
          expiredCount: 0,
          mostUrgentBatch: expiring[0],
          // Action required only if there are critical (0-7 days) non-expired batches
          actionRequired: expiring.some(b => b.daysRemaining >= 0 && b.daysRemaining < 7),
        };
        setExpiringBatchesSummary(summary);
      } else {
        setExpiringBatchesSummary(undefined);
      }
    } catch (error) {
      console.error('Error loading expiring batches:', error);
    } finally {
      setExpiringBatchesLoading(false);
    }
  };

  const getInventoryStats = () => {
    const totalItems = inventory.length;
    const totalValue = inventory.reduce((sum, item) => sum + (item.availableQuantity * item.pricePerUnit), 0);
    const lowStockItems = inventory.filter(item => (item.availableQuantity / item.quantity) < 0.2).length;

    // PESO Expiring: Only count items expiring within next 30 days (NOT already expired)
    const today = new Date();
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    const pesoExpiringItems = inventory.filter(item => {
      const expiryDate = new Date(item.pesoCompliance.expiryDate);
      // Only count if expiry is in the future AND within 30 days
      return expiryDate > today && expiryDate <= thirtyDaysFromNow;
    }).length;

    return { totalItems, totalValue, lowStockItems, pesoExpiringItems };
  };

  const stats = getInventoryStats();

  // Helper function to count active filters
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (batchFilters.productType && batchFilters.productType !== 'ALL') count++;
    if (batchFilters.qualityGrades && batchFilters.qualityGrades.length > 0) count += batchFilters.qualityGrades.length;
    if (batchFilters.pesoCompliant !== null && batchFilters.pesoCompliant !== undefined) count++;
    if (batchFilters.expiryRange && batchFilters.expiryRange !== 'ALL') count++;
    if (batchFilters.storageLocation) count++;
    if (batchFilters.lowStockOnly) count++;
    if (batchFilters.minPurity !== undefined) count++;
    if (batchFilters.maxPurity !== undefined) count++;
    if (batchFilters.minQuantity !== undefined) count++;
    return count;
  };

  // Reset filters
  const handleResetFilters = () => {
    setBatchFilters({
      productType: 'ALL',
      qualityGrades: [],
      pesoCompliant: null,
      expiryRange: 'ALL',
      storageLocation: '',
      lowStockOnly: false,
      minPurity: undefined,
      maxPurity: undefined,
      minQuantity: undefined
    });
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: BatchFilters) => {
    setBatchFilters(newFilters);
  };

  /**
   * Handle Update Quality action
   * US-BATCH-003: Convert InventoryItem to BatchResponse for dialog
   */
  const handleUpdateQuality = (item: InventoryItem) => {
    // Convert InventoryItem to BatchResponse format
    const batchResponse: BatchResponse = {
      id: parseInt(item.id),
      batchNumber: item.batchNumber,
      productType: item.gasType === 'compressed' ? 'COMPRESSED' : item.gasType === 'liquefied' ? 'LIQUIFIED' : 'BIO_CNG',
      productionDate: item.productionDate instanceof Date ? item.productionDate.toISOString().split('T')[0] : item.productionDate.toString(),
      expiryDate: item.expiryDate instanceof Date ? item.expiryDate.toISOString().split('T')[0] : item.expiryDate?.toString() || '',
      initialQuantity: item.quantity,
      currentQuantity: item.quantity,
      availableQuantity: item.availableQuantity,
      allocatedQuantity: item.quantity - item.availableQuantity,
      quantityUnit: item.unit === 'cubic_meters' ? 'CUBIC_METERS' : item.unit === 'kg' ? 'KG' : 'LITERS',
      qualityGrade: item.qualityGrade || 'A',
      purityPercentage: item.quality?.purity || item.purityPercentage || 95,
      pressure: item.quality?.pressure,
      storageLocation: item.location?.plantId || 'Unknown',
      batchStatus: item.status === 'available' ? 'AVAILABLE' : item.status === 'reserved' ? 'ALLOCATED' : 'SOLD',
      pesoCompliant: item.pesoCompliance?.status === 'valid',
      pesoCertificate: item.pesoCompliance ? {
        number: item.pesoCompliance.certificateNumber,
        inspector: item.pesoCompliance.inspector,
        issueDate: item.pesoCompliance.issueDate instanceof Date ? item.pesoCompliance.issueDate.toISOString() : item.pesoCompliance.issueDate.toString(),
        expiryDate: item.pesoCompliance.expiryDate instanceof Date ? item.pesoCompliance.expiryDate.toISOString() : item.pesoCompliance.expiryDate.toString(),
      } : undefined,
      createdBy: 'System',
      createdAt: new Date().toISOString(),
    };

    setSelectedBatchForUpdate(batchResponse);
    setShowUpdateQualityDialog(true);
  };

  /**
   * Handle Mark PESO Compliant action
   * US-BATCH-004: Convert InventoryItem to BatchResponse for dialog
   */
  const handleMarkPesoCompliant = (item: InventoryItem) => {
    // Convert InventoryItem to BatchResponse format
    const batchResponse: BatchResponse = {
      id: parseInt(item.id),
      batchNumber: item.batchNumber,
      productType: item.gasType === 'compressed' ? 'COMPRESSED' : item.gasType === 'liquefied' ? 'LIQUIFIED' : 'BIO_CNG',
      productionDate: item.productionDate instanceof Date ? item.productionDate.toISOString().split('T')[0] : item.productionDate.toString(),
      expiryDate: item.expiryDate instanceof Date ? item.expiryDate.toISOString().split('T')[0] : item.expiryDate?.toString() || '',
      initialQuantity: item.quantity,
      currentQuantity: item.quantity,
      availableQuantity: item.availableQuantity,
      allocatedQuantity: item.quantity - item.availableQuantity,
      quantityUnit: item.unit === 'cubic_meters' ? 'CUBIC_METERS' : item.unit === 'kg' ? 'KG' : 'LITERS',
      qualityGrade: item.qualityGrade || 'A',
      purityPercentage: item.quality?.purity || item.purityPercentage || 95,
      pressure: item.quality?.pressure,
      storageLocation: item.location?.plantId || 'Unknown',
      batchStatus: item.status === 'available' ? 'AVAILABLE' : item.status === 'reserved' ? 'ALLOCATED' : 'SOLD',
      pesoCompliant: item.pesoCompliance?.status === 'valid',
      pesoCertificate: item.pesoCompliance ? {
        number: item.pesoCompliance.certificateNumber,
        inspector: item.pesoCompliance.inspector,
        issueDate: item.pesoCompliance.issueDate instanceof Date ? item.pesoCompliance.issueDate.toISOString() : item.pesoCompliance.issueDate.toString(),
        expiryDate: item.pesoCompliance.expiryDate instanceof Date ? item.pesoCompliance.expiryDate.toISOString() : item.pesoCompliance.expiryDate.toString(),
      } : undefined,
      createdBy: 'System',
      createdAt: new Date().toISOString(),
    };

    setSelectedBatchForPeso(batchResponse);
    setShowPesoDialog(true);
  };

  /**
   * Handle Quality Update success
   * US-BATCH-003: Refresh inventory after updating quality
   */
  const handleQualityUpdateSuccess = async () => {
    // Reload inventory to get updated data
    try {
      const response = await salesService.getInventory(filters);
      if (response.success) {
        setInventory(response.data);
        setFilteredInventory(response.data);
      }
    } catch (error) {
      console.error('Error reloading inventory:', error);
    }
  };

  /**
   * Handle PESO compliance success
   * US-BATCH-004: Refresh inventory after marking PESO compliant
   */
  const handlePesoComplianceSuccess = async () => {
    // Reload inventory to get updated data
    try {
      const response = await salesService.getInventory(filters);
      if (response.success) {
        setInventory(response.data);
        setFilteredInventory(response.data);
      }
    } catch (error) {
      console.error('Error reloading inventory:', error);
    }
  };

  /**
   * US-BATCH-006: Handle expiring batch quick actions
   */
  const handleViewExpiringBatchDetails = (batch: ExpiringBatch) => {
    // Open expiring batch details dialog with the batch data
    setSelectedExpiringBatch(batch);
    setShowExpiringBatchDetailsDialog(true);
  };

  // Read-only details view for stock alerts — quantity is owned by the
  // manufacturing unit / sales allocation, so editing here is disallowed.
  // Pre-populates sessionStorage with the converted BatchResponse so the
  // dialog renders the real values from inventory rather than fetching a
  // (potentially empty) detail record from the API.
  const handleViewAlertDetails = (item: InventoryItem) => {
    const batchResponse: BatchResponse = {
      id: parseInt(item.id),
      batchNumber: item.batchNumber,
      productType: item.gasType === 'compressed' ? 'COMPRESSED' : item.gasType === 'liquefied' ? 'LIQUIFIED' : 'BIO_CNG',
      productionDate: item.productionDate instanceof Date ? item.productionDate.toISOString().split('T')[0] : item.productionDate.toString(),
      expiryDate: item.expiryDate instanceof Date ? item.expiryDate.toISOString().split('T')[0] : item.expiryDate?.toString() || '',
      initialQuantity: item.quantity,
      currentQuantity: item.quantity,
      availableQuantity: item.availableQuantity,
      allocatedQuantity: item.quantity - item.availableQuantity,
      quantityUnit: item.unit === 'cubic_meters' ? 'CUBIC_METERS' : item.unit === 'kg' ? 'KG' : 'LITERS',
      qualityGrade: item.qualityGrade || 'A',
      purityPercentage: item.quality?.purity ?? item.purityPercentage ?? 0,
      pressure: item.quality?.pressure,
      storageLocation: item.location?.plantId || 'Unknown',
      batchStatus: item.status === 'available' ? 'AVAILABLE' : item.status === 'reserved' ? 'ALLOCATED' : 'SOLD',
      pesoCompliant: item.pesoCompliance?.status === 'valid',
      pesoCertificate: item.pesoCompliance ? {
        number: item.pesoCompliance.certificateNumber,
        inspector: item.pesoCompliance.inspector,
        issueDate: item.pesoCompliance.issueDate instanceof Date ? item.pesoCompliance.issueDate.toISOString() : item.pesoCompliance.issueDate.toString(),
        expiryDate: item.pesoCompliance.expiryDate instanceof Date ? item.pesoCompliance.expiryDate.toISOString() : item.pesoCompliance.expiryDate.toString(),
      } : undefined,
      createdBy: 'System',
      createdAt: new Date().toISOString(),
    };
    try {
      sessionStorage.setItem(`batch_${item.id}`, JSON.stringify(batchResponse));
    } catch (e) {
      console.warn('Failed to cache batch in sessionStorage:', e);
    }
    setSelectedBatchForDetails(item.id);
    setBatchDetailsReadOnly(true);
    setShowBatchDetailsDialog(true);
  };

  const handleCreateDiscountOrder = async (batch: ExpiringBatch) => {
    try {
      // Calculate dynamic discount based on days remaining
      // Critical (0-7 days): 30-50% discount
      // High (7-14 days): 15-30% discount
      // Medium (15-30 days): 5-15% discount
      let discountPercentage = 5;

      if (batch.daysRemaining < 7) {
        discountPercentage = 50; // Critical - max discount
      } else if (batch.daysRemaining < 14) {
        discountPercentage = 30; // High urgency
      } else {
        discountPercentage = 15; // Medium urgency
      }

      // Navigate to sales order creation with batch and discount pre-filled
      navigate(`/sales/create-order?batchId=${batch.id}&batchNumber=${batch.batchNumber}&discount=${discountPercentage}&quantity=${batch.availableQuantity}`);

      toast.success(
        `Discount Order Creation`,
        {
          description: `Creating discount order for ${batch.batchNumber} with ${discountPercentage}% discount`
        }
      );
    } catch (error) {
      console.error('Error creating discount order:', error);
      toast.error('Failed to create discount order', {
        description: 'Please try again or contact support.'
      });
    }
  };

  const handleMarkForDisposal = async (batch: ExpiringBatch) => {
    try {
      const confirmed = window.confirm(
        `⚠️ Are you sure you want to mark batch ${batch.batchNumber} for disposal?\n\n` +
        `This action will:\n` +
        `• Move batch to disposal queue\n` +
        `• Prevent any future sales\n` +
        `• Notify operations team\n\n` +
        `This action cannot be easily reversed.`
      );

      if (!confirmed) {
        return;
      }

      // Call API to mark batch for disposal
      // await inventoryBatchService.markForDisposal(batch.id);

      // Remove batch from expiring batches list
      setExpiringBatches(prev => prev.filter(b => b.id !== batch.id));

      // Update summary
      setExpiringBatchesSummary(prev =>
        prev ? {
          ...prev,
          totalCritical: batch.urgencyLevel === 'CRITICAL' ? (prev.totalCritical || 0) - 1 : prev.totalCritical,
          totalHigh: batch.urgencyLevel === 'HIGH' ? (prev.totalHigh || 0) - 1 : prev.totalHigh,
          totalMedium: batch.urgencyLevel === 'MEDIUM' ? (prev.totalMedium || 0) - 1 : prev.totalMedium,
        } : prev
      );

      // Show success notification
      toast.success(
        `Batch Marked for Disposal`,
        {
          description: `${batch.batchNumber} has been marked for immediate disposal. Operations team has been notified.`
        }
      );
    } catch (error) {
      console.error('Error marking batch for disposal:', error);
      toast.error('Failed to mark batch for disposal', {
        description: 'Please try again or contact support.'
      });
    }
  };

  const handleContactSalesTeam = async (batch: ExpiringBatch) => {
    try {
      const message = `🚨 EXPIRING BATCH ALERT\n` +
        `Batch: ${batch.batchNumber}\n` +
        `Product: ${batch.productType}\n` +
        `Expires in: ${batch.daysRemaining} days\n` +
        `Available Qty: ${batch.availableQuantity} ${batch.quantityUnit}\n` +
        `Quality Grade: ${batch.qualityGrade}\n` +
        `Storage: ${batch.storageLocation}`;

      // In a real implementation, this would send notifications
      // Options: Email, SMS, In-app notification, Slack, Teams
      // await notificationService.notifySalesTeam({
      //   batchId: batch.id,
      //   message,
      //   urgency: batch.urgencyLevel,
      //   type: 'EXPIRING_BATCH_ALERT'
      // });

      // For now, copy to clipboard and show success
      const textToCopy = `${message}\n\nQuick Actions:\n- Create Discount Order\n- Mark for Disposal`;

      // Try to copy to clipboard
      try {
        await navigator.clipboard.writeText(textToCopy);
        toast.success(
          `Sales Team Alert Created`,
          {
            description: `Alert for ${batch.batchNumber} copied to clipboard. Ready to share with sales team via email, Slack, or Teams.`
          }
        );
      } catch {
        // Fallback if clipboard is not available
        toast.success(
          `Sales Team Notified`,
          {
            description: `Alert created for ${batch.batchNumber}. Consider creating a discount order to move inventory quickly.`
          }
        );
      }
    } catch (error) {
      console.error('Error contacting sales team:', error);
      toast.error('Failed to create alert', {
        description: 'Please try again or contact support.'
      });
    }
  };

  const handleManualRefresh = async () => {
    try {
      setIsRefreshing(true);
      setLoadError(null);
      const response = await salesService.getInventory(filters);
      if (response.success) {
        setInventory(response.data);
        setFilteredInventory(response.data);
        loadExpiringBatches(response.data);
        setLastUpdated(new Date());
        setLoadError(null);
        toast.success('Inventory refreshed successfully', {
          description: `${response.data.length} batch${response.data.length !== 1 ? 'es' : ''} loaded at ${new Date().toLocaleTimeString()}`,
        });
      } else {
        setLoadError(response.message || 'Failed to load inventory. Please try again.');
        toast.error('Failed to refresh inventory', {
          description: 'Could not fetch the latest batch data. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error refreshing inventory:', error);
      setLoadError('A network error occurred. Please check your connection.');
      toast.error('Failed to refresh inventory', {
        description: 'A network error occurred. Please check your connection and try again.',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Inventory Management</h1>
          <p className="text-gray-600">Monitor gas inventory, batches, and PESO compliance</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Manual Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2"
          >
            <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </Button>

          {/* Auto-Refresh Toggle */}
          <Button
            variant={autoRefreshEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
            className="flex items-center gap-2"
            title={autoRefreshEnabled ? "Auto-refresh enabled (every 30s)" : "Auto-refresh disabled"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {autoRefreshEnabled ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
          </Button>

          {/* Last Updated */}
          {lastUpdated && (
            <div className="text-xs text-gray-500 px-2">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold">{stats.totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inventory Value</p>
                <p className="text-2xl font-bold">₹{formatCurrency(stats.totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold">{stats.lowStockItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">PESO Expiring</p>
                <p className="text-2xl font-bold">{stats.pesoExpiringItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by batch, product, status (low stock, consumed, available, grade a...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
                aria-label="Search inventory batches"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                  tabIndex={0}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className={getActiveFilterCount() > 0 ? 'border-blue-500 text-blue-600' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {getActiveFilterCount() > 0 && (
                <Badge className="ml-2 bg-blue-500">{getActiveFilterCount()}</Badge>
              )}
            </Button>
          </div>

          {/* Filters Panel */}
          {showFiltersPanel && (
            <BatchFiltersPanel
              filters={batchFilters}
              onFiltersChange={handleFiltersChange}
              onReset={handleResetFilters}
              activeFilterCount={getActiveFilterCount()}
            />
          )}

          {/* Results count and active search indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Showing {filteredInventory.length} of {inventory.length} items</span>
            {searchTerm && (
              <Badge variant="secondary" className="flex items-center gap-1 cursor-pointer" onClick={() => setSearchTerm('')}>
                Search: "{searchTerm}"
                <X className="h-3 w-3" />
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* US-BATCH-006: Expiring Batches Widget */}
      {expiringBatchesSummary && expiringBatchesSummary.totalExpiringCount > 0 && (
        <ExpiringBatchesWidget
          summary={expiringBatchesSummary}
          loading={expiringBatchesLoading}
          onViewDetails={() => {
            // Switch to expiring batches tab
            setActiveTab('expiring');
          }}
        />
      )}

      {/* Inventory Grid */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="table">Table View</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="expiring" className="flex items-center gap-2">
            Expiring Batches
            {expiringBatches.length > 0 && (
              <Badge className="ml-2 bg-red-600 text-white">{expiringBatches.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="space-y-6">
          {loading ? (
            <div className="text-center py-8">Loading inventory...</div>
          ) : loadError && inventory.length === 0 ? (
            <div className="text-center py-12 px-6">
              <AlertTriangle className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Failed to Load Inventory
              </h3>
              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                {loadError}
              </p>
              <Button onClick={handleManualRefresh} disabled={isRefreshing} className="mt-2">
                {isRefreshing ? 'Retrying...' : 'Retry Now'}
              </Button>
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="text-center py-12 px-6">
              <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || getActiveFilterCount() > 0 ? 'No Matching Batches Found' : 'No Ready-to-Sale Batches Available'}
              </h3>
              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                {searchTerm || getActiveFilterCount() > 0
                  ? 'Try adjusting your search or filter criteria.'
                  : 'All batches are automatically created from the biogas production system.'}
              </p>
              {searchTerm || getActiveFilterCount() > 0 ? (
                <Button variant="outline" onClick={() => { setSearchTerm(''); handleResetFilters(); }}>
                  Clear Filters
                </Button>
              ) : (
                <p className="text-sm text-gray-500">
                  Only batches with "Ready for Sale" status are displayed here.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInventory.map((item) => (
                <InventoryCard
                  key={item.id}
                  item={item}
                  onMarkPesoCompliant={handleMarkPesoCompliant}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="table">
          <Card>
            <CardHeader>
              <CardTitle>Inventory Table View</CardTitle>
              <CardDescription>Detailed inventory listing with sortable columns</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading inventory...</div>
              ) : loadError && inventory.length === 0 ? (
                <div className="text-center py-8 text-red-500">{loadError}</div>
              ) : filteredInventory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No inventory items found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold">Batch #</th>
                        <th className="text-left py-3 px-4 font-semibold">Product Type</th>
                        <th className="text-right py-3 px-4 font-semibold">Available</th>
                        <th className="text-right py-3 px-4 font-semibold">Total</th>
                        <th className="text-center py-3 px-4 font-semibold">Availability %</th>
                        <th className="text-left py-3 px-4 font-semibold">Location</th>
                        <th className="text-left py-3 px-4 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((item) => {
                        const availabilityPercent = (item.availableQuantity / item.quantity) * 100;
                        const isLowStock = availabilityPercent < 20;

                        return (
                          <tr key={item.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{item.batchNumber}</td>
                            <td className="py-3 px-4 font-medium">{item.gasType}</td>
                            <td className="py-3 px-4 text-right font-medium text-green-600">{item.availableQuantity.toLocaleString()} {item.unit}</td>
                            <td className="py-3 px-4 text-right font-medium">{item.quantity.toLocaleString()} {item.unit}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge className={isLowStock ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                                {availabilityPercent.toFixed(1)}%
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm">
                              {item.manufacturingUnitName ? (
                                <div>
                                  <div className="font-medium">{item.manufacturingUnitName}</div>
                                  {item.manufacturingUnitLocation && (
                                    <div className="text-xs text-gray-500">{item.manufacturingUnitLocation}</div>
                                  )}
                                </div>
                              ) : (
                                `${item.location.plantId}${item.location.tankId ? ` - ${item.location.tankId}` : ''}`
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {availabilityPercent === 0 ? (
                                <Badge className="bg-gray-300 text-gray-800">consumed</Badge>
                              ) : availabilityPercent < 20 ? (
                                <Badge className="bg-orange-200 text-orange-800">low stock</Badge>
                              ) : (
                                <Badge className={getStatusColor(item.status)}>{item.status}</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="space-y-4">
            {/* Critical Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                  Critical Alerts (Low Stock)
                </CardTitle>
                <CardDescription>Batches with less than 10% availability</CardDescription>
              </CardHeader>
              <CardContent>
                {inventory.filter(item => (item.availableQuantity / item.quantity) < 0.1).length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    No critical alerts - all batches have sufficient stock
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inventory.filter(item => (item.availableQuantity / item.quantity) < 0.1).map(item => {
                      const availabilityPercent = (item.availableQuantity / item.quantity) * 100;
                      return (
                        <div key={item.id} className="flex items-center justify-between p-4 border-l-4 border-l-red-600 rounded bg-red-50">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{item.batchNumber}</div>
                            <div className="text-sm text-gray-700 mt-1">
                              Available: <span className="font-medium text-red-600">{item.availableQuantity.toLocaleString()} {item.unit}</span> / Total: {item.quantity.toLocaleString()} {item.unit}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Stock Level: <span className="font-semibold">{availabilityPercent.toFixed(1)}%</span> • Location: {item.location.plantId}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Badge className="bg-red-100 text-red-800 whitespace-nowrap">Critical</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewAlertDetails(item)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Warning Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                  Warning Alerts (Low to Medium Stock)
                </CardTitle>
                <CardDescription>Batches with 10-30% availability</CardDescription>
              </CardHeader>
              <CardContent>
                {inventory.filter(item => {
                  const percent = (item.availableQuantity / item.quantity) * 100;
                  return percent >= 0.1 && percent < 0.3;
                }).length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    No warning alerts - all batches have good stock levels
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inventory.filter(item => {
                      const percent = (item.availableQuantity / item.quantity) * 100;
                      return percent >= 0.1 && percent < 0.3;
                    }).map(item => {
                      const availabilityPercent = (item.availableQuantity / item.quantity) * 100;
                      return (
                        <div key={item.id} className="flex items-center justify-between p-4 border-l-4 border-l-orange-500 rounded bg-orange-50">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{item.batchNumber}</div>
                            <div className="text-sm text-gray-700 mt-1">
                              Available: <span className="font-medium text-orange-600">{item.availableQuantity.toLocaleString()} {item.unit}</span> / Total: {item.quantity.toLocaleString()} {item.unit}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Stock Level: <span className="font-semibold">{availabilityPercent.toFixed(1)}%</span> • Location: {item.location.plantId}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Badge className="bg-orange-100 text-orange-800 whitespace-nowrap">Warning</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewAlertDetails(item)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expiry Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                  Expiry Alerts (Within 30 Days)
                </CardTitle>
                <CardDescription>Batches expiring soon - action recommended</CardDescription>
              </CardHeader>
              <CardContent>
                {expiringBatches.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    No batches expiring within 30 days
                  </div>
                ) : (
                  <div className="space-y-3">
                    {expiringBatches.map(batch => (
                      <div key={batch.id} className={`flex items-center justify-between p-4 rounded border-l-4 ${
                        batch.daysRemaining < 0
                          ? 'border-l-red-600 bg-red-50'
                          : batch.daysRemaining < 7
                          ? 'border-l-red-500 bg-red-50'
                          : 'border-l-orange-500 bg-orange-50'
                      }`}>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{batch.batchNumber}</div>
                          <div className="text-sm text-gray-700 mt-1">
                            Expiry Date: <span className="font-medium">{new Date(batch.expiryDate).toLocaleDateString()}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Quality: {batch.qualityGrade} • Purity: {batch.purityPercentage.toFixed(1)}% • Available: {batch.availableQuantity.toLocaleString()} {batch.quantityUnit}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Badge className={
                            batch.daysRemaining < 0
                              ? 'bg-red-100 text-red-800'
                              : batch.daysRemaining < 7
                              ? 'bg-red-100 text-red-800'
                              : 'bg-orange-100 text-orange-800'
                          }>
                            {batch.daysRemaining < 0 ? 'Expired' : `${batch.daysRemaining}d left`}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const inventoryItem = inventory.find(item => item.batchNumber === batch.batchNumber);
                              if (inventoryItem) {
                                handleViewAlertDetails(inventoryItem);
                              }
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* US-BATCH-006: Expiring Batches Tab */}
        <TabsContent value="expiring">
          <ExpiringBatchesAlertTab
            batches={expiringBatches}
            loading={expiringBatchesLoading}
            onViewDetails={handleViewExpiringBatchDetails}
            onCreateDiscountOrder={handleCreateDiscountOrder}
            onMarkForDisposal={handleMarkForDisposal}
            onContactSales={handleContactSalesTeam}
          />
        </TabsContent>
      </Tabs>

      {/* US-BATCH-003: Update Quality Dialog */}
      {selectedBatchForUpdate && (
        <UpdateQualityDialog
          batch={selectedBatchForUpdate}
          open={showUpdateQualityDialog}
          onOpenChange={setShowUpdateQualityDialog}
          onSuccess={handleQualityUpdateSuccess}
        />
      )}

      {/* US-BATCH-004: PESO Compliance Dialog */}
      {selectedBatchForPeso && (
        <PesoComplianceDialog
          batch={selectedBatchForPeso}
          open={showPesoDialog}
          onOpenChange={setShowPesoDialog}
          onSuccess={handlePesoComplianceSuccess}
        />
      )}

      {/* US-BATCH-006: Batch Details Dialog */}
      <BatchDetailsDialog
        batchId={selectedBatchForDetails}
        open={showBatchDetailsDialog}
        onOpenChange={(open) => {
          setShowBatchDetailsDialog(open);
          if (!open) setBatchDetailsReadOnly(false);
        }}
        readOnly={batchDetailsReadOnly}
      />

      {/* Expiring Batch Details Dialog */}
      <ExpiringBatchDetailsDialog
        batch={selectedExpiringBatch}
        open={showExpiringBatchDetailsDialog}
        onOpenChange={setShowExpiringBatchDetailsDialog}
      />
    </div>
  );
};

export default InventoryManagement;